import { differenceInDays } from "date-fns";
import { parseAllocations, nightlyTotal } from "./roomAllocations";

export interface ItineraryCostItem {
  item_type: string;
  cost?: number | null;
  manual_price?: number | null;
  quantity?: number | null;
  check_in?: string | null;
  check_out?: string | null;
  service_start?: string | null;
  service_end?: string | null;
  universal_fee_kind?: string | null;
  staff_categories?: string[] | null;
  room_guest_type?: string | null;
  staff_role_tags?: string[] | null;
  session_count?: number | null;
  room_allocations?: unknown;
}

/**
 * Calculates the number of nights for an accommodation item
 */
export function calculateAccommodationNights(checkIn: string | null | undefined, checkOut: string | null | undefined): number {
  if (!checkIn || !checkOut) return 0;
  
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  
  // Normalize to start of day to ensure accurate night count
  checkInDate.setHours(0, 0, 0, 0);
  checkOutDate.setHours(0, 0, 0, 0);
  
  const nights = differenceInDays(checkOutDate, checkInDate);
  return nights > 0 ? nights : 0;
}

/**
 * Inclusive day count for a service window (Guide / S&T / Private Chef
 * universal fees). End − Start + 1 (minimum 1).
 */
export function calculateUniversalFeeDays(
  start: string | null | undefined,
  end: string | null | undefined
): number {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  const diff = differenceInDays(e, s) + 1;
  return diff > 0 ? diff : 0;
}

/**
 * Calculates the total cost for an itinerary item, accounting for:
 * - Custom items: use manual_price directly
 * - Accommodation: cost × nights × quantity (rooms)
 * - Other items: cost × quantity
 */
export function calculateItemTotalCost(item: ItineraryCostItem): number {
  // Use cost or manual_price (custom items store in manual_price)
  const cost = item.cost || item.manual_price || 0;
  const quantity = item.quantity || 1;

  // Recurring sessions (e.g. Mon–Fri classes): cost × quantity × sessions
  const sessions = item.session_count && item.session_count > 1 ? item.session_count : 1;

  // Custom items: cost × quantity (same as standard items)
  if (item.item_type === 'custom') {
    return cost * quantity * sessions;
  }

  // Accommodation: multiply by number of nights
  if (item.item_type === 'accommodation' && item.check_in && item.check_out) {
    const nights = calculateAccommodationNights(item.check_in, item.check_out);
    // Mixed-room cards: price each room line exactly (the mirrored blended cost is rounded).
    const lines = parseAllocations(item.room_allocations).filter((l) => l.rooms > 0);
    if (lines.length > 0) {
      return nightlyTotal(lines) * nights;
    }
    // quantity represents number of rooms for accommodation
    return cost * nights * quantity;
  }

  // Multi-day universal fees: Guide / S&T / Private Chef.
  // New model: cost × days × quantity (× staff categories for S&T multi-select).
  // Legacy rows without service_start/service_end fall back to using
  // `quantity` as the day count, preserving old behaviour.
  if (
    item.item_type === 'universal_fee' &&
    (item.universal_fee_kind === 'guiding' || item.universal_fee_kind === 'assistant_guide' ||
      item.universal_fee_kind === 'st' ||
      item.universal_fee_kind === 'chef')
  ) {
    const hasWindow = !!(item.service_start && item.service_end);
    const days = hasWindow
      ? calculateUniversalFeeDays(item.service_start, item.service_end)
      : (item.quantity || 1);
    // Chef is billed as a flat per-day rate — no per-person / quantity multiplier.
    const people = item.universal_fee_kind === 'chef'
      ? 1
      : (hasWindow ? (item.quantity || 1) : 1);
    const staffMultiplier =
      item.universal_fee_kind === 'st'
        ? (item.staff_categories?.length || 1)
        : 1;
    return cost * days * people * staffMultiplier;
  }

  // Default: cost × quantity × sessions
  return cost * quantity * sessions;
}

/**
 * Scales a single itinerary item for a new participant count.
 * - Accommodation items: returned unchanged (rooms/nights are not auto-scaled)
 * - All other items (including custom): quantity scales proportionally with pax
 * - Falls back to the original item if basePax is missing/zero
 */
export function scaleItemForPax<T extends { item_type?: string; quantity?: number | null }>(
  item: T,
  basePax: number | null | undefined,
  newPax: number | null | undefined
): T {
  if (!basePax || !newPax || basePax <= 0 || newPax <= 0 || basePax === newPax) {
    return item;
  }
  if (item.item_type === 'accommodation' || item.item_type === 'universal_fee') {
    return item;
  }
  // Staff accommodation rows are operational (driver/guide rooms) and never scale with guest pax.
  if ((item as any).room_guest_type === 'staff') {
    return item;
  }
  const originalQty = item.quantity || 1;
  const scaledQty = Math.max(1, Math.round((originalQty * newPax) / basePax));
  return { ...item, quantity: scaledQty };
}

/**
 * Scales an array of itinerary items for a new participant count.
 */
export function scaleItemsForPax<T extends { item_type?: string; quantity?: number | null }>(
  items: T[],
  basePax: number | null | undefined,
  newPax: number | null | undefined
): T[] {
  return items.map((item) => scaleItemForPax(item, basePax, newPax));
}

/**
 * Returns true if the items list contains any accommodation entries
 * (used to display the "rooms not auto-adjusted" warning).
 */
export function hasAccommodationItems(items: Array<{ item_type?: string }>): boolean {
  return items.some((i) => i.item_type === 'accommodation');
}
